import React, { useState, useEffect, useRef } from "react";

// ─── Brand Colors ────────────────────────────────────────────────
const C = {
  navy: "#121531",
  navyLight: "#1c2045",
  cyan: "#42A4D9",
  cyanLight: "#6bbde6",
  gold: "#D4A843",
  white: "#FFFFFF",
  offWhite: "#F4F6FA",
  muted: "#8892A4",
  border: "rgba(66,164,217,0.18)",
};

// ─── Budget Data ──────────────────────────────────────────────────
const MEETINGS = [
  {
    id: "arvo",
    label: "ARVO",
    date: "May 3–7",
    location: "Denver, CO",
    color: C.cyan,
    learners: "25–50",
    individual: { live: 99000, enduring: 59000, infographic: 9500 },
    combined:   { live: 99000, enduring: 59000, infographic: 9500 }, // no discount
    monthStart: 4, monthEnd: 4,
  },
  {
    id: "asrs",
    label: "ASRS",
    date: "July 15–18",
    location: "Montréal, Canada",
    color: "#5B9BD5",
    learners: "40–60",
    individual: { live: 119000, enduring: 59000, infographic: 9500 },
    combined:   { live: 111000, enduring: 59000, infographic: 0 }, // $8K off live, infographic waived
    monthStart: 6, monthEnd: 6,
  },
  {
    id: "aao",
    label: "AAO",
    date: "Oct 9–12",
    location: "New Orleans, LA",
    color: "#4A7EC0",
    learners: "75–100",
    individual: { live: 119000, enduring: 59000, infographic: 9500 },
    combined:   { live: 111000, enduring: 59000, infographic: 0 }, // $8K off live, infographic waived
    monthStart: 9, monthEnd: 9,
  },
];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmt(n) {
  return "$" + n.toLocaleString();
}

function calcIndividual(selected) {
  return selected.reduce((acc, m) => acc + m.individual.live + m.individual.enduring + m.individual.infographic, 0);
}

function calcCombined(selected) {
  if (selected.length === 0) return 0;
  return selected.reduce((a, m) => a + m.combined.live + m.combined.enduring + m.combined.infographic, 0);
}

// ─── Animated Number ─────────────────────────────────────────────
function AnimNum({ value, duration = 600 }) {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);
  const raf = useRef(null);

  useEffect(() => {
    const start = prev.current;
    const end = value;
    const startTime = performance.now();
    const animate = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + (end - start) * ease));
      if (p < 1) raf.current = requestAnimationFrame(animate);
      else prev.current = end;
    };
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf.current);
  }, [value, duration]);

  return <span>${display.toLocaleString()}</span>;
}

// ─── Slide Shell ─────────────────────────────────────────────────
function Slide({ children, style = {} }) {
  return (
    <div style={{
      minHeight: "100%",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      padding: "48px 56px",
      boxSizing: "border-box",
      ...style,
    }}>
      {children}
    </div>
  );
}

function SlideHeader({ label }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 4, height: 28, background: C.cyan, borderRadius: 2 }} />
        <span style={{
          fontFamily: "'Georgia', serif",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: C.cyan,
        }}>{label}</span>
      </div>
      <div style={{ height: 1, background: C.border, marginTop: 12 }} />
    </div>
  );
}

// ─── SLIDE 2: Introduction ────────────────────────────────────────
function Slide2() {
  return (
    <Slide>
      <SlideHeader label="BeLite Bio · 2026 Combined Initiative" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, flex: 1 }}>
        {/* Left: Letter */}
        <div>
          <p style={{ fontSize: 13, color: C.offWhite, lineHeight: 1.85, marginTop: 0 }}>
            Dear Medical Education Grants,
          </p>
          <p style={{ fontSize: 13, color: C.offWhite, lineHeight: 1.85 }}>
            Catalyst is proposing three separate educational grant requests developed in collaboration with{" "}
            <strong style={{ color: C.white }}>Quan Dong Nguyen, MD, MSc</strong>:
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "20px 0" }}>
            {MEETINGS.map((m, i) => (
              <div key={m.id} style={{
                display: "flex", alignItems: "center", gap: 14,
                background: "rgba(255,255,255,0.03)",
                border: `1px solid ${C.border}`,
                borderLeft: `3px solid ${m.color}`,
                borderRadius: 8,
                padding: "12px 16px",
              }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: m.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: C.white, flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.white }}>Symposium at {m.label}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{m.date} · {m.location}</div>
                </div>
              </div>
            ))}
          </div>

          <p style={{ fontSize: 13, color: C.offWhite, lineHeight: 1.85 }}>
            Catalyst provides value through the strategic development of cost-effective, outcomes-driven education. Enclosed you will find slides that demonstrate our strategy for initiative development and cost savings specific to these requests.
          </p>
          <p style={{ fontSize: 13, color: C.cyan, lineHeight: 1.85, fontStyle: "italic" }}>
            This combined initiative would save approximately <strong style={{ color: C.gold }}>$35,000</strong>.
          </p>

          <div style={{ marginTop: 24 }}>
            <div style={{ fontSize: 13, color: C.offWhite }}>Sincerely,</div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: 16, color: C.white, marginTop: 8 }}>Louis Settembrino, CHCP</div>
            <div style={{ fontSize: 11, color: C.muted }}>Founder & CEO · Catalyst Medical Education</div>
          </div>
        </div>

        {/* Right: Identity column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
          {/* Logo — large, centered, no box border */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px 0 8px 0",
          }}>
            <img
              src="/catalyst-logo-cyan.png"
              alt="Catalyst Medical Education"
              style={{ width: "85%", maxWidth: 320, mixBlendMode: "screen" }}
            />
          </div>

          {/* Who We Are */}
          <div style={{
            background: `linear-gradient(135deg, rgba(66,164,217,0.12), rgba(26,52,107,0.15))`,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: "20px 24px",
            flex: 1,
          }}>
            <div style={{ fontSize: 10, color: C.cyan, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 8 }}>Who We Are</div>
            <p style={{ fontSize: 13, color: C.offWhite, lineHeight: 1.8, margin: 0 }}>
              An <strong style={{ color: C.white }}>independent medical education organization</strong> dedicated to developing education that accelerates learning and improves care.
            </p>
          </div>

          {/* How We Provide Value */}
          <div style={{
            background: `rgba(212,168,67,0.07)`,
            border: `1px solid rgba(212,168,67,0.2)`,
            borderRadius: 12,
            padding: "20px 24px",
            flex: 1,
          }}>
            <div style={{ fontSize: 10, color: C.gold, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 8 }}>How We Provide Value</div>
            <p style={{ fontSize: 13, color: C.offWhite, lineHeight: 1.8, margin: 0 }}>
              Developing <strong style={{ color: C.white }}>cost-effective, outcomes-driven education</strong> that leverages principles of instructional design and adult learning to speed the translation of clinical data into everyday practice.
            </p>
          </div>

          {/* Tagline */}
          <div style={{
            textAlign: "center",
            padding: "10px 0 4px 0",
            borderTop: `1px solid ${C.border}`,
          }}>
            <div style={{
              fontFamily: "'Georgia', serif",
              fontSize: 20,
              fontWeight: 400,
              lineHeight: 1.3,
              color: C.white,
              fontStyle: "italic",
            }}>
              Accelerate Learning.{" "}
              <span style={{ color: C.cyan }}>Improve Care.</span>
            </div>
          </div>
        </div>
      </div>
    </Slide>
  );
}

// ─── SLIDE 3: Initiative Overview ────────────────────────────────
function Slide3() {
  const formats = [
    {
      icon: "🎤",
      title: "Live Symposia",
      color: C.cyan,
      desc: "1–2 hour interactive symposia held during ARVO, ASRS, and/or AAO",
      bullets: ["3 expert faculty", "Dynamic format", "Compelling evidence-based discussions", "Patient scenarios", "Expert Q&A"],
    },
    {
      icon: "💻",
      title: "On-Demand Web Activity",
      color: C.gold,
      desc: "Content captured and converted into on-demand, interactive web activities",
      bullets: ["Reinforcing resources", "Original infographics", "Suggested readings", "1,000+ learner reach"],
    },
    {
      icon: "📣",
      title: "Audience Generation",
      color: "#7EC8E3",
      desc: "Powered by leading medical publishers and audience generation partners",
      bullets: ["Springer/Nature", "Elsevier", "JAMA Network", "Wiley"],
    },
  ];

  return (
    <Slide>
      <SlideHeader label="Initiative Overview" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20, marginBottom: 28 }}>
        {formats.map(f => (
          <div key={f.title} style={{
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${C.border}`,
            borderTop: `3px solid ${f.color}`,
            borderRadius: 10,
            padding: "24px 22px",
          }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>{f.icon}</div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: 15, color: C.white, marginBottom: 8 }}>{f.title}</div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>{f.desc}</div>
            <ul style={{ margin: 0, paddingLeft: 16, display: "flex", flexDirection: "column", gap: 5 }}>
              {f.bullets.map(b => (
                <li key={b} style={{ fontSize: 12, color: C.offWhite, lineHeight: 1.5 }}>{b}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Cost amortization callout */}
      <div style={{
        background: `linear-gradient(90deg, rgba(66,164,217,0.12), rgba(212,168,67,0.08))`,
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        padding: "18px 28px",
        display: "flex", alignItems: "center", gap: 20,
      }}>
        <div style={{ fontSize: 28, flexShrink: 0 }}>💡</div>
        <div>
          <div style={{ fontSize: 12, color: C.gold, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>
            Cost-effective, Outcomes-driven Education
          </div>
          <div style={{ fontSize: 13, color: C.offWhite, lineHeight: 1.7 }}>
            This combined initiative will amortize the investment in content development — the most expensive budget item —{" "}
            <strong style={{ color: C.white }}>across 3 symposia, 3 web-based activities, and infographics.</strong>
          </div>
        </div>
      </div>
    </Slide>
  );
}

// ─── SLIDE 4: Timeline ────────────────────────────────────────────
function Slide4() {
  const rows = [
    { label: "ARVO Symposium", color: MEETINGS[0].color, type: "live", months: [4], loc: "Denver" },
    { label: "ASRS Symposium", color: MEETINGS[1].color, type: "live", months: [6], loc: "Montréal" },
    { label: "AAO Symposium",  color: MEETINGS[2].color, type: "live", months: [9], loc: "New Orleans" },
    { label: "Web Activity (ARVO archive)", color: MEETINGS[0].color, type: "web", months: [5,6,7] },
    { label: "Web Activity (ASRS archive)", color: MEETINGS[1].color, type: "web", months: [7,8,9] },
    { label: "Web Activity (AAO archive)",  color: MEETINGS[2].color, type: "web", months: [10,11,12] },
    { label: "Reinforcing Resources / Infographics", color: C.gold, type: "resource", months: [4,5,6,7,8,9,10,11] },
  ];

  return (
    <Slide>
      <SlideHeader label="Initiative Timeline" />
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ width: 200, textAlign: "left", paddingBottom: 12, paddingRight: 12, color: C.muted, fontWeight: 500, fontSize: 11 }}>Activity</th>
              {MONTHS.map(m => (
                <th key={m} style={{ textAlign: "center", paddingBottom: 12, color: C.muted, fontWeight: 500, fontSize: 11, minWidth: 42 }}>{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                <td style={{ paddingRight: 12, paddingTop: 5, paddingBottom: 5, color: C.offWhite, fontSize: 11, whiteSpace: "nowrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: row.color, flexShrink: 0 }} />
                    {row.label}
                    {row.loc && <span style={{ color: C.muted, fontSize: 10 }}>· {row.loc}</span>}
                  </div>
                </td>
                {MONTHS.map((_, mi) => {
                  const active = row.months.includes(mi);
                  const first = active && !row.months.includes(mi - 1);
                  const last  = active && !row.months.includes(mi + 1);
                  return (
                    <td key={mi} style={{ padding: "5px 2px" }}>
                      {active ? (
                        <div style={{
                          height: 22,
                          background: row.type === "live"
                            ? row.color
                            : row.type === "web"
                            ? `${row.color}55`
                            : `${row.color}44`,
                          borderRadius: first && last ? 6 : first ? "6px 0 0 6px" : last ? "0 6px 6px 0" : 0,
                          border: row.type === "live" ? "none" : `1px solid ${row.color}66`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {first && row.type === "live" && (
                            <span style={{ fontSize: 9, color: C.white, fontWeight: 700, letterSpacing: "0.04em", paddingLeft: 5 }}>
                              {row.label.split(" ")[0]}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div style={{ height: 22, background: "rgba(255,255,255,0.02)", borderRadius: 3 }} />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 24, marginTop: 20, flexWrap: "wrap" }}>
        {[
          { label: "Live Symposium", style: { background: C.cyan, borderRadius: 4, width: 14, height: 14 } },
          { label: "Web Activity (archive)", style: { background: `${C.cyan}55`, border: `1px solid ${C.cyan}66`, borderRadius: 4, width: 14, height: 14 } },
          { label: "Reinforcing Resources", style: { background: `${C.gold}44`, border: `1px solid ${C.gold}66`, borderRadius: 4, width: 14, height: 14 } },
        ].map(l => (
          <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={l.style} />
            <span style={{ fontSize: 11, color: C.muted }}>{l.label}</span>
          </div>
        ))}
      </div>
    </Slide>
  );
}

// ─── SLIDE 5: Budget Calculator ───────────────────────────────────
function Slide5({ selected, setSelected }) {
  const toggle = (id) => {
    setSelected(prev =>
      prev.some(m => m.id === id) ? prev.filter(m => m.id !== id) : [...prev, MEETINGS.find(m => m.id === id)]
    );
  };

  const indTotal = calcIndividual(selected);
  const combTotal = calcCombined(selected);
  const savings = indTotal - combTotal;

  return (
    <Slide>
      <SlideHeader label="Initiative Savings" />

      {/* Meeting toggles */}
      <div style={{ display: "flex", gap: 10, marginBottom: 28 }}>
        {MEETINGS.map(m => {
          const on = selected.some(s => s.id === m.id);
          return (
            <button key={m.id} onClick={() => toggle(m.id)} style={{
              padding: "8px 20px",
              borderRadius: 8,
              border: `1.5px solid ${on ? m.color : "rgba(255,255,255,0.15)"}`,
              background: on ? `${m.color}22` : "transparent",
              color: on ? m.color : C.muted,
              fontSize: 12, fontWeight: 600, cursor: "pointer",
              transition: "all 0.2s",
            }}>
              {on ? "✓ " : ""}{m.label}
            </button>
          );
        })}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: C.muted }}>Toggle requests to explore savings</span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28, flex: 1 }}>
        {/* Left: Individual requests */}
        <div>
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 14 }}>Individual Requests</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {MEETINGS.map((m, i) => {
              const on = selected.some(s => s.id === m.id);
              const total = m.individual.live + m.individual.enduring + m.individual.infographic;
              return (
                <div key={m.id} style={{
                  background: on ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.01)",
                  border: `1px solid ${on ? m.color + "44" : "rgba(255,255,255,0.05)"}`,
                  borderLeft: `3px solid ${on ? m.color : "rgba(255,255,255,0.08)"}`,
                  borderRadius: 8,
                  padding: "14px 16px",
                  opacity: on ? 1 : 0.4,
                  transition: "all 0.25s",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: on ? C.white : C.muted }}>
                      Request {i + 1} — {m.label}
                    </span>
                    <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: on ? C.white : C.muted, fontWeight: 700 }}>
                      {fmt(total)}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {[
                      [`Live Symposium (${m.learners} learners)`, m.individual.live],
                      ["Enduring Web Activity (1,000 learners)", m.individual.enduring],
                      ["Reinforcing Resources / Infographics", m.individual.infographic],
                    ].map(([l, v]) => (
                      <div key={l} style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 11, color: C.muted }}>{l}</span>
                        <span style={{ fontSize: 11, color: C.offWhite }}>{fmt(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* R1+R2+R3 subtotal */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              borderTop: `1px solid ${C.border}`,
              paddingTop: 10, paddingRight: 6,
            }}>
              <span style={{ fontSize: 12, color: C.muted }}>
                {selected.length > 0 ? selected.map(m => `Request ${MEETINGS.findIndex(x => x.id === m.id)+1}`).join(" + ") : "No selections"} Total
              </span>
              <span style={{ fontFamily: "'Georgia', serif", fontSize: 15, color: C.white, fontWeight: 700 }}>
                <AnimNum value={indTotal} />
              </span>
            </div>
          </div>
        </div>

        {/* Right: Combined initiative + savings */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Cost reduction strategies */}
          <div style={{
            background: "rgba(255,255,255,0.03)",
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: "18px 20px",
          }}>
            <div style={{ fontSize: 11, color: C.cyan, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>
              Strategies That Reduce Costs
            </div>
            {[
              "Content amortization across 3 symposia and reinforcing features",
              "Negotiated rates with all collaborators: accredited providers, audience generation partners",
              "Less providers to manage — uniform status updates and outcomes reports",
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: 9 }}>
                <div style={{ width: 18, height: 18, borderRadius: "50%", background: `${C.cyan}22`, border: `1px solid ${C.cyan}44`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                  <span style={{ fontSize: 9, color: C.cyan, fontWeight: 700 }}>✓</span>
                </div>
                <span style={{ fontSize: 12, color: C.offWhite, lineHeight: 1.6 }}>{s}</span>
              </div>
            ))}
          </div>

          {/* Combined total */}
          <div style={{
            background: `linear-gradient(135deg, rgba(66,164,217,0.12), rgba(26,52,107,0.18))`,
            border: `1px solid ${C.cyan}44`,
            borderRadius: 10,
            padding: "18px 20px",
          }}>
            <div style={{ fontSize: 11, color: C.cyan, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>
              Full Initiative (After Savings)
            </div>
            {selected.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
                {selected.map(m => (
                  <div key={m.id} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: C.muted }}>
                      Live — {m.label}{m.id !== "arvo" ? <span style={{ color: C.gold, marginLeft: 4, fontSize: 10 }}>-$8,000</span> : null}
                    </span>
                    <span style={{ fontSize: 11, color: C.offWhite }}>{fmt(m.combined.live)}</span>
                  </div>
                ))}
                {selected.map(m => (
                  <div key={m.id + "e"} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: C.muted }}>Enduring — {m.label}</span>
                    <span style={{ fontSize: 11, color: C.offWhite }}>{fmt(m.combined.enduring)}</span>
                  </div>
                ))}
                {selected.map(m => m.combined.infographic > 0 && (
                  <div key={m.id + "i"} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: C.muted }}>
                      Infographic — {m.label} <span style={{ fontSize: 10, color: C.muted }}>(shared across all 3)</span>
                    </span>
                    <span style={{ fontSize: 11, color: C.offWhite }}>{fmt(m.combined.infographic)}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
              <span style={{ fontSize: 13, color: C.white }}>Full Initiative Total</span>
              <span style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: C.cyan, fontWeight: 700 }}>
                <AnimNum value={combTotal} />
              </span>
            </div>
          </div>

          {/* Savings callout */}
          <div style={{
            background: `linear-gradient(135deg, rgba(212,168,67,0.15), rgba(212,168,67,0.06))`,
            border: `1.5px solid ${C.gold}55`,
            borderRadius: 10,
            padding: "18px 20px",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 11, color: C.gold, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>
              Total Savings
            </div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: 36, color: C.gold, fontWeight: 400 }}>
              <AnimNum value={savings} />
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>vs. submitting individually</div>
          </div>

          {/* Note */}
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: `1px dashed rgba(255,255,255,0.1)`,
            borderRadius: 8,
            padding: "12px 16px",
          }}>
            <span style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
              <strong style={{ color: C.offWhite }}>Please note:</strong> All activities can be supported individually and tailored to your budget (e.g. reduce or increase audience generation tactics).
            </span>
          </div>
        </div>
      </div>
    </Slide>
  );
}

// ─── SUPPLEMENTAL MODAL ───────────────────────────────────────────
function SupplementalModal({ onClose }) {
  const [tab, setTab] = useState("outcomes");

  const tabs = [
    { id: "outcomes", label: "Outcomes Evaluation" },
    { id: "outcomesPlus", label: "Outcomes+" },
    { id: "instructional", label: "Instructional Design" },
    { id: "value", label: "Value of Catalyst" },
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.75)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: C.navyLight,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        width: "100%", maxWidth: 760, maxHeight: "80vh",
        overflow: "hidden",
        display: "flex", flexDirection: "column",
      }} onClick={e => e.stopPropagation()}>
        {/* Modal header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 28px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontFamily: "'Georgia', serif", fontSize: 18, color: C.white }}>Catalyst Capabilities</div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.06)", border: "none", color: C.muted, fontSize: 18, cursor: "pointer", width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${C.border}`, padding: "0 28px", overflowX: "auto" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: "none", border: "none",
              borderBottom: `2px solid ${tab === t.id ? C.cyan : "transparent"}`,
              color: tab === t.id ? C.cyan : C.muted,
              fontSize: 12, fontWeight: 500,
              padding: "12px 16px",
              cursor: "pointer", whiteSpace: "nowrap",
              transition: "all 0.15s",
            }}>{t.label}</button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ overflowY: "auto", padding: "28px", flex: 1 }}>
          {tab === "outcomes" && (
            <div>
              <h3 style={{ fontFamily: "'Georgia', serif", color: C.white, marginTop: 0 }}>Meaningful Outcomes Evaluation</h3>
              <p style={{ fontSize: 13, color: C.offWhite, lineHeight: 1.8 }}>
                Our goal is to utilize data visualization to provide valuable outcomes reports that assess changes to the <strong style={{ color: C.white }}>knowledge, skill, competence, attitude, behavior, and/or performance</strong> of learners.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
                {[
                  { label: "Methods", text: "Pre-, post-, and follow-up assessments using various question formats (Likert scale, matrix, case vignettes)" },
                  { label: "Reporting", text: "Real-time data collection and reporting that enables ongoing analysis of learner engagement, reach, and demographics" },
                  { label: "Key Findings", text: "Immediate and longer-term changes, barriers to change, and potential patient impact" },
                  { label: "Publication Ready", text: "Evaluated for the potential for submission for publication/presentation" },
                ].map(item => (
                  <div key={item.label} style={{ background: "rgba(66,164,217,0.06)", border: `1px solid ${C.border}`, borderRadius: 8, padding: "16px 18px" }}>
                    <div style={{ fontSize: 11, color: C.cyan, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: C.offWhite, lineHeight: 1.7 }}>{item.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "outcomesPlus" && (
            <div>
              <h3 style={{ fontFamily: "'Georgia', serif", color: C.white, marginTop: 0 }}>Outcomes+ — Enhanced Reporting</h3>
              <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.8 }}>An enhanced extension of our standard outcomes reporting</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
                {[
                  ["Cohort Analysis", "Analyses by specialty, geography, practice setting, patient population, etc."],
                  ["Statistical Validation", "ANOVA, chi-square — statistical analyses to validate significant differences across cohorts"],
                  ["Predictive Modeling", "Identify the most impactful variables differentiating learner groups"],
                  ["Financial Impact", "Estimates based on patient reach and reported changes in clinical practice"],
                ].map(([title, desc]) => (
                  <div key={title} style={{ display: "flex", gap: 14, background: "rgba(212,168,67,0.05)", border: `1px solid rgba(212,168,67,0.15)`, borderRadius: 8, padding: "14px 18px" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.gold, flexShrink: 0, marginTop: 4 }} />
                    <div>
                      <div style={{ fontSize: 13, color: C.white, fontWeight: 600, marginBottom: 4 }}>{title}</div>
                      <div style={{ fontSize: 12, color: C.offWhite, lineHeight: 1.6 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "instructional" && (
            <div>
              <h3 style={{ fontFamily: "'Georgia', serif", color: C.white, marginTop: 0 }}>Additional Instructional Design Features</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {[
                  { title: "Patient Learning Portal", url: "https://catalystmeded.com/PT-P", desc: "Comprehensive patient education portal utilizing infographics and translations — literacy level appropriate, mobile friendly." },
                  { title: "Video Demonstrations", url: "https://catalystmeded.com/suprachoroidal", desc: "Video-based education for key concepts — cadaver-based anatomy training, suprachoroidal injection demonstrations." },
                  { title: "Patient Videos", url: "https://catalystmeded.com/PTV", desc: "Facilitates increased sensitivity to patient needs, promoting recognition of the patient as an individual." },
                ].map(item => (
                  <div key={item.title} style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}`, borderRadius: 8, padding: "18px 20px" }}>
                    <div style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: C.white, marginBottom: 6 }}>{item.title}</div>
                    <div style={{ fontSize: 12, color: C.offWhite, lineHeight: 1.7, marginBottom: 8 }}>{item.desc}</div>
                    <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: C.cyan, textDecoration: "none" }}>
                      {item.url} ↗
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "partners" && (
            <div>
              <h3 style={{ fontFamily: "'Georgia', serif", color: C.white, marginTop: 0 }}>Trusted Partners & Collaborators</h3>
              {[
                { label: "Accredited Providers", items: ["PIM Medical Education", "Johns Hopkins Medicine", "UT College of Pharmacy", "UNMC"] },
                { label: "Medical Societies", items: ["AMCP", "CMSC", "IOMSN", "ACC"] },
                { label: "Audience Generation Partners", items: ["Springer/Nature", "Elsevier", "JAMA Network", "Healio", "Medscape", "Wiley", "Lippincott/Wolters Kluwer"] },
                { label: "Global Education Partners", items: ["Global Education Group", "Siyemi Learning", "InVivo"] },
              ].map(group => (
                <div key={group.label} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: C.cyan, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 }}>{group.label}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {group.items.map(item => (
                      <span key={item} style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 12px", fontSize: 12, color: C.offWhite }}>{item}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "value" && (
            <div>
              <h3 style={{ fontFamily: "'Georgia', serif", color: C.white, marginTop: 0 }}>The Value of Working with Catalyst</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {[
                  { icon: "🏆", title: "Unparalleled Service", desc: "Dedicated team, uniform status updates, and outcomes reports — less providers to manage." },
                  { icon: "🎯", title: "Strategic Instructional Design", desc: "Adult learning principles with global reach and evidence-based curriculum development." },
                  { icon: "💰", title: "Cost-Effective Education", desc: "Content amortization, negotiated partner rates, and combined initiative savings." },
                  { icon: "📊", title: "Track Record of Success", desc: "Extensive experience across therapeutic areas with outcomes-driven, measurable results." },
                ].map(v => (
                  <div key={v.title} style={{ background: "rgba(66,164,217,0.06)", border: `1px solid ${C.border}`, borderRadius: 10, padding: "20px" }}>
                    <div style={{ fontSize: 28, marginBottom: 10 }}>{v.icon}</div>
                    <div style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: C.white, marginBottom: 8 }}>{v.title}</div>
                    <div style={{ fontSize: 12, color: C.offWhite, lineHeight: 1.7 }}>{v.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────
export default function App() {
  const [slide, setSlide] = useState(0);
  const [selected, setSelected] = useState(MEETINGS);
  const [showSupp, setShowSupp] = useState(false);

  const slides = [
    { label: "Introduction", component: <Slide2 /> },
    { label: "Overview",     component: <Slide3 /> },
    { label: "Timeline",     component: <Slide4 /> },
    { label: "Savings",      component: <Slide5 selected={selected} setSelected={setSelected} /> },
  ];

  return (
    <div style={{
      background: C.navy,
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      fontFamily: "'Georgia', serif",
      color: C.white,
    }}>
      {/* Top nav */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 32px",
        height: 52,
        borderBottom: `1px solid ${C.border}`,
        background: "rgba(18,21,49,0.95)",
        backdropFilter: "blur(8px)",
        position: "sticky", top: 0, zIndex: 100,
      }}>
        {/* Slide tabs */}
        <div style={{ display: "flex", gap: 2 }}>
          {slides.map((s, i) => (
            <button key={i} onClick={() => setSlide(i)} style={{
              background: "none", border: "none",
              borderBottom: `2px solid ${i === slide ? C.cyan : "transparent"}`,
              color: i === slide ? C.white : C.muted,
              fontSize: 12, fontWeight: i === slide ? 600 : 400,
              padding: "0 14px", height: 52,
              cursor: "pointer",
              transition: "all 0.15s",
            }}>{s.label}</button>
          ))}
        </div>

        {/* Right: savings badge + supplemental */}
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            background: `${C.gold}18`,
            border: `1px solid ${C.gold}44`,
            borderRadius: 20,
            padding: "4px 14px",
            fontSize: 12,
            color: C.gold,
          }}>
            Savings: <AnimNum value={calcIndividual(selected) - calcCombined(selected)} duration={400} />
          </div>
          <button onClick={() => setShowSupp(true)} style={{
            background: "rgba(66,164,217,0.1)",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            color: C.cyan,
            fontSize: 12,
            padding: "6px 14px",
            cursor: "pointer",
          }}>
            Capabilities ↗
          </button>
        </div>
      </div>

      {/* Slide content */}
      <div style={{ flex: 1, maxWidth: 1100, width: "100%", margin: "0 auto", alignSelf: "stretch", display: "flex", flexDirection: "column" }}>
        {slides[slide].component}
      </div>

      {/* Bottom nav */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 16,
        padding: "14px 32px",
        borderTop: `1px solid ${C.border}`,
      }}>
        <button onClick={() => setSlide(s => Math.max(0, s - 1))} disabled={slide === 0} style={{
          background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`,
          borderRadius: 8, color: slide === 0 ? C.muted : C.white,
          padding: "7px 20px", fontSize: 12, cursor: slide === 0 ? "default" : "pointer",
        }}>← Prev</button>
        <div style={{ display: "flex", gap: 8 }}>
          {slides.map((_, i) => (
            <div key={i} onClick={() => setSlide(i)} style={{
              width: i === slide ? 24 : 8, height: 8, borderRadius: 4,
              background: i === slide ? C.cyan : "rgba(255,255,255,0.15)",
              cursor: "pointer", transition: "all 0.2s",
            }} />
          ))}
        </div>
        <button onClick={() => setSlide(s => Math.min(slides.length - 1, s + 1))} disabled={slide === slides.length - 1} style={{
          background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`,
          borderRadius: 8, color: slide === slides.length - 1 ? C.muted : C.white,
          padding: "7px 20px", fontSize: 12, cursor: slide === slides.length - 1 ? "default" : "pointer",
        }}>Next →</button>
      </div>

      {/* Supplemental modal */}
      {showSupp && <SupplementalModal onClose={() => setShowSupp(false)} />}
    </div>
  );
}
